
import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { X, Plus } from "lucide-react";

interface KeywordsDisplayProps {
  keywords: string[];
  isEditable?: boolean;
  updateBusinessData?: (key: string, value: string[]) => void;
}

const KeywordsDisplay: React.FC<KeywordsDisplayProps> = ({ 
  keywords, 
  isEditable = false,
  updateBusinessData 
}) => {
  const [newKeyword, setNewKeyword] = useState("");

  if (!keywords || keywords.length === 0 && !isEditable) return null;
  
  const handleAddKeyword = () => {
    if (!newKeyword.trim() || !updateBusinessData) return;
    
    // Don't add duplicate keywords (case insensitive)
    if (keywords.some(k => k.toLowerCase() === newKeyword.toLowerCase())) {
      setNewKeyword("");
      return;
    }
    
    const updatedKeywords = [...keywords, newKeyword.trim()];
    updateBusinessData("business_tags", updatedKeywords);
    setNewKeyword("");
  };
  
  const handleRemoveKeyword = (keywordToRemove: string) => {
    if (!updateBusinessData) return;
    
    const updatedKeywords = keywords.filter(k => k !== keywordToRemove);
    updateBusinessData("business_tags", updatedKeywords);
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  return (
    <div className="space-y-3">
      <Label>Business Keywords</Label>
      
      {isEditable && (
        <div className="flex gap-2 mb-3">
          <Input
            placeholder="Add a keyword (e.g., marketing, tech, healthcare)"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyDown={handleKeyPress}
          />
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleAddKeyword}
            disabled={!newKeyword.trim()}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {keywords.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {keywords.map((keyword, index) => (
            <span 
              key={index} 
              className="px-3 py-1 bg-secondary text-secondary-foreground rounded-md text-xs flex items-center"
            >
              {keyword}
              {isEditable && (
                <button 
                  className="ml-2 text-muted-foreground hover:text-destructive"
                  onClick={() => handleRemoveKeyword(keyword)}
                  aria-label={`Remove ${keyword} keyword`}
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No keywords added yet. Keywords help tailor AI responses to your business domain.
        </p>
      )}
    </div>
  );
};

export default KeywordsDisplay;
